import type { Ref } from 'vue'
import { useEventListener } from '@vueuse/core'

interface UseAcademyScrollRestoreOptions {
  activeSelector: string
  activeOffset: number
  isVisible: Ref<boolean>
  scrollKey: Ref<string>
  stateKey: string
}

export function useAcademyScrollRestore(
  scrollContainerRef: Ref<HTMLElement | null>,
  options: UseAcademyScrollRestoreOptions,
) {
  const scrollPositions = useState<Record<string, number>>(options.stateKey, () => ({}))

  function saveScroll() {
    if (!scrollContainerRef.value) {
      return
    }

    scrollPositions.value = {
      ...scrollPositions.value,
      [options.scrollKey.value]: scrollContainerRef.value.scrollTop,
    }
  }

  function restoreScroll() {
    if (!import.meta.client || !options.isVisible.value) {
      return
    }

    const setScrollPosition = () => {
      if (!scrollContainerRef.value) {
        return
      }

      const savedScrollPosition = scrollPositions.value[options.scrollKey.value]
      if (savedScrollPosition !== undefined) {
        scrollContainerRef.value.scrollTop = savedScrollPosition
        return
      }

      const activeArticleLink = scrollContainerRef.value.querySelector<HTMLElement>(options.activeSelector)
      if (!activeArticleLink) {
        return
      }

      scrollContainerRef.value.scrollTop = Math.max(activeArticleLink.offsetTop - options.activeOffset, 0)
    }

    nextTick(() => {
      setScrollPosition()
      window.requestAnimationFrame(() => {
        setScrollPosition()
        window.requestAnimationFrame(setScrollPosition)
      })
    })
  }

  useEventListener(scrollContainerRef, 'scroll', saveScroll)

  onBeforeRouteLeave(() => {
    saveScroll()
  })

  onBeforeRouteUpdate(() => {
    saveScroll()
  })

  watch([options.isVisible, options.scrollKey], restoreScroll, {
    immediate: true,
    flush: 'post',
  })

  watch(scrollContainerRef, restoreScroll, {
    flush: 'post',
  })

  onMounted(restoreScroll)

  return {
    restoreScroll,
    saveScroll,
  }
}
